import { useMemo, useSyncExternalStore } from 'react';
import type { GroupedTask, MinifiedTask } from '@gcv/shared';
import { TaskInternalType } from '@gcv/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { TasksApi } from 'api/TasksApi';
import { FI_TASK_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries/useUser';
import { getFiBankStore } from 'stores/FiBankStore';
import type { TaskDetails } from 'ui/apps/fi/tasks/tasks.model';
import { getCachedTask, getDecoratedTask, getTaskDetails, TASK_LIST_KEYS } from 'ui/apps/fi/util/tasks';
import { isUuid } from 'util/strings.util';

export function useFiTask(taskId: string) {
  const { userBankId } = useUser();
  const bankGroups = getFiBankStore().bank.groups;
  const tasksApi = new TasksApi();
  const haveTaskListsLoaded = useHaveTaskListsLoaded();

  const { data: tasks, isPending } = useQuery<(MinifiedTask | GroupedTask)[]>({
    // Grouped tasks only ever come from the loaded lists, so the lookup has to wait on them.
    enabled: !!userBankId.length && !!taskId.length && haveTaskListsLoaded,
    queryKey: [FI_TASK_KEY, userBankId, taskId],
    queryFn: () => {
      const cachedTask = getCachedTask(taskId);

      if (cachedTask) {
        return [cachedTask];
      }

      return isUuid(taskId) ? tasksApi.getTaskById(userBankId, taskId) : [];
    },
    meta: {
      snackbarErrorMessage:
        'There was an issue loading this task. Please contact support if the problem persists.'
    }
  });

  const fiTask = useMemo<TaskDetails | null>(() => {
    if (!tasks?.length) {
      return null;
    }

    const decoratedTask = getDecoratedTask(tasks[0]);

    // Monitoring reviews are always presented as a group, even with a single subtask.
    if ((tasks[0] as MinifiedTask).internal_type === TaskInternalType.MONITORING_REVIEWS) {
      decoratedTask.isGrouped = true;
    }

    return getTaskDetails(decoratedTask, bankGroups);
  }, [tasks, bankGroups]);

  return { fiTask, isLoadingFiTask: isPending, isFiTaskMissing: !isPending && !fiTask };
}

/**
 * Tracks whether the task lists a lookup reads can be trusted: one has loaded and none is still
 * in flight. Either page the task drawer opens from loads only its own list.
 */
function useHaveTaskListsLoaded() {
  const queryCache = useQueryClient().getQueryCache();

  return useSyncExternalStore(
    (onCacheChange) => queryCache.subscribe(onCacheChange),
    () => {
      const taskLists = TASK_LIST_KEYS.flatMap((taskListKey) =>
        queryCache.findAll({ queryKey: [taskListKey] })
      );

      return (
        taskLists.some((list) => list.state.status !== 'pending') &&
        taskLists.every((list) => list.state.fetchStatus !== 'fetching')
      );
    }
  );
}
